import { useState, useEffect, Fragment, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow
} from 'components/table';
import {
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  flexRender,
  getSortedRowModel
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import messages from 'messages.js';
import { FormattedMessage, useIntl } from 'react-intl';
import PersonSelect from 'components/input/PersonSelect';
import { Button } from 'components/button';
import Icon from 'components/icon/Icon';
import TooltipPopper from 'components/tooltip/TooltipPopper';
import { sort } from 'utils/sort';
import Person from 'models/Person';

const columnHelper = createColumnHelper();

function SyncPersonsWithIntegrationReviewTable({
  integration,
  persons,
  syncedPersons,
  externalPersons,
  onSelectPerson,
  onConfirmPerson,
  selected
}) {
  const intl = useIntl();
  const [rowSelection, setRowSelection] = useState({});

  const [tableData, setTableData] = useState([]);

  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (externalPersons) {
      if (tableData.length === 0) {
        const externalDataSorted = sort(externalPersons, {
          keys: [
            { key: 'status', desc: true },
            { key: 'firstname' },
            { key: 'lastname' }
          ]
        });

        const syncedDataSorted = sort(syncedPersons, {
          keys: [{ key: 'firstname' }, { key: 'lastname' }]
        });

        setTableData([...externalDataSorted, ...syncedDataSorted]);
      } else {
        // Update the table data while keeping the current sorting and selection
        setTableData(prev => {
          return prev.map(person => {
            const updatedPerson = syncedPersons.find(
              ep =>
                ep.integrations?.[integration]?.externalPersonId ===
                person.externalPersonId
            );
            if (!updatedPerson) {
              const extPerson = externalPersons.find(
                ep => ep.externalPersonId === person.externalPersonId
              );
              if (extPerson) {
                return { ...person, ...extPerson };
              }
            }
            return updatedPerson ? updatedPerson : person;
          });
        });
      }
    }
  }, [externalPersons, syncedPersons]);

  const columns = [
    columnHelper.group({
      id: `${integration}-persons`,
      header: () => (
        <div className="u-margin-auto">
          <FormattedMessage
            {...messages.tableHeaderIntegrationPersons}
            values={{
              integration
            }}
          />
        </div>
      ),
      columns: [
        columnHelper.accessor('firstname', {
          header: intl.formatMessage(messages.tableHeaderFirstname),
          cell: ({ row }) => {
            const person = row.original;
            if (person.constructor === Person) {
              if (person.integrations[integration]?.meta?.firstname) {
                return (
                  <span>
                    {person.integrations[integration]?.meta?.firstname}
                  </span>
                );
              } else {
                return <span>{person.firstname}</span>;
              }
            } else {
              return <span>{person.firstname}</span>;
            }
          },
          size: 165,
          footer: info => info.column.id
        }),
        columnHelper.accessor('lastname', {
          header: intl.formatMessage(messages.tableHeaderLastname),
          cell: ({ row }) => {
            const person = row.original;
            if (person.constructor === Person) {
              if (person.integrations[integration]?.meta?.lastname) {
                return (
                  <span>
                    {person.integrations[integration]?.meta?.lastname}
                  </span>
                );
              } else {
                return <span>{person.lastname}</span>;
              }
            } else {
              return <span>{person.lastname}</span>;
            }
          },
          size: 165,
          footer: info => info.column.id
        }),
        columnHelper.accessor('birthdate', {
          header: intl.formatMessage(messages.tableHeaderBirthdate),
          size: 120,
          cell: ({ cell }) => {
            const date = new Date(cell.getValue());
            if (date.toString() === 'Invalid Date') {
              return '-';
            } else {
              return (
                <span>
                  {date.toLocaleDateString(intl.locale, {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                  })}
                </span>
              );
            }
          },
          footer: info => info.column.id
        }),
        columnHelper.accessor('status', {
          header: intl.formatMessage(messages.tableHeaderStatus),
          size: 60,
          cell: ({ row }) => {
            const person = row.original;

            if (person.constructor === Person) {
              return (
                <Icon
                  id="validation-check"
                  smallIcon
                  extraClassNames="u-margin-auto"
                />
              );
            } else if (row.original.personId) {
              return (
                <TooltipPopper
                  id="probably-match-found"
                  extraClassNames="u-margin-auto o-flex"
                  rightCenter
                  aboveModal
                  onMouseEnter={() => true}
                  onFocus={() => true}
                  onMouseLeave={() => false}
                  onBlur={() => false}
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    return true;
                  }}
                  labelObject={
                    <Icon
                      id="target"
                      smallMediumIcon
                      style={{
                        color: 'var(--color-notice)'
                      }}
                    />
                  }
                  styleRoot={{
                    zIndex: 1
                  }}
                >
                  <FormattedMessage
                    {...messages.tableIconTooltipPossibleMatch}
                  />
                </TooltipPopper>
              );
            }
          },
          sortingFn: 'auto',
          sortDescFirst: true,
          footer: info => info.column.id
        })
      ]
    }),
    columnHelper.group({
      id: 'hylyght-persons',
      header: () => (
        <div className="u-margin-auto">
          <FormattedMessage {...messages.tableHeaderHyPersons} />
        </div>
      ),
      columns: [
        columnHelper.accessor('personId', {
          header: intl.formatMessage(messages.tableHeaderPerson),
          size: 240,
          cell: ({ row }) => {
            const person = row.original;

            const selectedPerson = persons.find(
              p => p.value === row.original.personId
            );

            if (person.constructor === Person) {
              return (
                <span>{`${person.firstname} ${person.lastname} (${person.birthdate.toLocaleDateString(
                  intl.locale,
                  {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                  }
                )})`}</span>
              );
            }

            return (row.original.personId &&
              (person.status === 'match' || person.status === 'loading')) ||
              row.getIsSelected() ? (
              <PersonSelect
                value={selectedPerson}
                options={persons}
                isMulti={false}
                disabled={person.status === 'loading'}
                placeholder={intl.formatMessage(messages.selectUserPlaceholder)}
                onChange={person =>
                  onSelectPerson(person, row.original.externalPersonId)
                }
                style={{ width: '100%' }}
              />
            ) : (
              <Button
                rounded
                grey
                onClick={row.getToggleSelectedHandler()}
                style={{
                  outline: '1px solid var(--color-primary-dark-10)'
                }}
              >
                <FormattedMessage {...messages.tableButtonSearchMatch} />
              </Button>
            );
            // }
          },
          footer: info => info.column.id
        }),
        columnHelper.display({
          id: 'actions',
          size: 160,
          cell: ({ row }) => {
            const person = row.original;

            if (row.original.personId && person.status === 'match') {
              return (
                <Button
                  rounded
                  grey
                  onClick={() => {
                    row.original.status = 'loading';
                    onConfirmPerson(person);
                  }}
                  style={{
                    outline: '1px solid var(--color-primary-dark-10)',
                    margin: 'auto'
                  }}
                >
                  <FormattedMessage {...messages.tableButtonConfirmMatch} />
                </Button>
              );
            } else if (row.original.personId && person.status === 'loading') {
              return (
                <Button
                  rounded
                  disabled
                  style={{
                    outline: '1px solid var(--color-primary-dark-10)',
                    margin: 'auto'
                  }}
                >
                  <FormattedMessage {...messages.tableButtonConfirming} />
                </Button>
              );
            } else if (person.constructor === Person) {
              return (
                <span className="u-margin-auto">
                  <FormattedMessage {...messages.tableMessageConfirmed} />
                </span>
              );
            } else {
              return (
                <span className={'c-table--vald__no-match u-margin-auto'}>
                  <FormattedMessage {...messages.tableMessageNotFound} />
                </span>
              );
            }
          },
          footer: info => info.column.id
        })
      ]
    })
  ];

  const personsTable = useReactTable({
    data: tableData,
    columns: columns,
    state: {
      // sorting,
      rowSelection
    },
    enableRowSelection: true,
    enableMultiRowSelection: false,
    onRowSelectionChange: setRowSelection,
    // onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false
  });

  useEffect(() => {
    if (selected.length > 0) {
      setRowSelection(
        selected.reduce((acc, person) => {
          const index = externalPersons.findIndex(a => a.row === person.row);
          acc[index] = true;
          return acc;
        }, {})
      );
    }
  }, [selected, externalPersons]);

  const topHeaderGroups = personsTable
    .getHeaderGroups()
    .filter(hg => hg.depth === 0)[0].headers;

  return (
    <div
      className="c-file-upload__content"
      ref={tableContainerRef}
      style={{
        overflow: 'auto',
        height: 'calc(100dvh - 42px - 320px)',
        position: 'relative'
      }}
    >
      <Table valueTable extraClassNames="c-table--import c-table--vald">
        <colgroup>
          {topHeaderGroups.map(h => {
            return (
              <col span={h.colSpan} className={`col-${h.id}`} key={h.id} />
            );
          })}
        </colgroup>
        <TableHeader
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 102,
            backgroundColor: 'var(--color-neutral-light)'
          }}
        >
          {personsTable.getHeaderGroups().map(headerGroup => (
            <TableRow
              key={headerGroup.id}
              extraClassNames="c-table__group-header"
            >
              {headerGroup.headers.map(header => {
                return (
                  <TableHeaderCell
                    key={header.id}
                    style={{
                      width: `${((header.getSize() / personsTable.getTotalSize()) * 100).toFixed(0)}%`,
                      color: 'inherit',
                      justifyContent: header.id === 'status' && 'center'
                    }}
                    colSpan={header.colSpan}
                    extraClassNames={{
                      'o-flex--justify-center': header.id === 'status'
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHeaderCell>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <VirtualizedTableBody
          table={personsTable}
          tableContainerRef={tableContainerRef}
          externalPersons={externalPersons}
        />
      </Table>
    </div>
  );
}

const VirtualizedTableBody = ({ table, tableContainerRef }) => {
  const { rows } = table.getRowModel();

  // Important: Keep the row virtualizer in the lowest component possible to avoid unnecessary re-renders.
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 51, //estimate row height for accurate scrollbar dragging
    getScrollElement: () => tableContainerRef.current,
    //measure dynamic row height, except in firefox because it measures table border height incorrectly
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? element => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5
  });

  return (
    <TableBody
      style={{
        position: 'relative',
        height: `${rowVirtualizer.getTotalSize()}px`
      }}
    >
      {rowVirtualizer.getVirtualItems().map(virtualRow => {
        const row = rows[virtualRow.index];

        return (
          <Fragment key={row.id}>
            <VirtualTableRow
              key={row.id}
              row={row}
              virtualRow={virtualRow}
              rowVirtualizer={rowVirtualizer}
              width={table.getTotalSize()}
            />
          </Fragment>
        );
      })}
    </TableBody>
  );
};

const VirtualTableRow = ({ row, virtualRow, rowVirtualizer, width }) => {
  const person = row.original;

  return (
    <TableRow
      extraClassNames={{
        /* selected */
        // 'c-table__row--selected': !row.original.personId && !row.original.status,
        'c-table__row--possible': person.personId && person.status === 'match',
        'c-table__row--warning': person.constructor === Person
      }}
      /* Ik weet het niet of onze Rows dynamisch hoeven te zijn */
      data-index={virtualRow.index} // needed for dynamic row height measurement
      ref={node => rowVirtualizer.measureElement(node)} // measure dynamic row height
      key={row.id}
      style={{
        display: 'flex', // Necessary
        position: 'absolute',
        height: 51,
        transform: `translateY(${virtualRow.start}px)`,
        width: '100%'
      }}
    >
      {row.getVisibleCells().map(cell => {
        return (
          <TableCell
            key={cell.id}
            style={{
              display: 'flex',
              width: `${((cell.column.getSize() / width) * 100).toFixed(0)}%`,
              color: 'inherit'
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

export default SyncPersonsWithIntegrationReviewTable;
